import React, {useEffect} from 'react';
import {observer} from 'mobx-react';
import {injectIntl} from 'react-intl';
import * as Sentry from '@sentry/react';

import {
  CreateDispatchPanelWrapper,
  Description,
  ButtonsContainer,
  MainContentWrapper,
  MediaUploaderWrapper,
  Label,
  SectionWrapper
} from './styles';
import messages from './messages';

import ModalsTextBox from 'components/ModalsTextBox';
import MediaUploader from 'components/MediaUploader';
import {withSidePanel} from 'components/SidePanel';
import {Button} from 'components/Button';
import Select from 'components/Select';

import {OpenDispatch} from 'api/dispatch/mutations';
import {useMutation} from '@apollo/client';
import {useStore} from 'hooks/useStore';
import {useIsDesktop, useLinesQuery, useDispatchTypesQuery, useDispatchMachinesQuery} from './hooks';

const CreateDispatchPanelComponent = ({popSidePanel, intl: {formatMessage}}) => {
  const store = useStore();

  const {
    notification,
    dispatch: {
      createDispatch,
      reset,
      feedbackText,
      setFeedbackText,
      setMediaUrl,
      canOpenDispatch,
      setSelectedLine,
      setSelectedMachine,
      setSelectedDispatchType,
      setIsUploading,
      maxFeedbackLength,
      loading,
      mediaUrl,
      selectedLineId,
      selectedMachineId,
      selectedDispatchTypeId
    },
    instructionPage: {activeStepId}
  } = store;

  const [openDispatchMutation] = useMutation(OpenDispatch, {
    errorPolicy: 'none'
  });
  const {lines, linesLoading, fetchMoreLines} = useLinesQuery();
  const {dispatchTypes, typesLoading, fetchMoreTypes} = useDispatchTypesQuery();
  const {dispatchMachinesQuery, dispatchMachines, machinesLoading, fetchMoreMachines} = useDispatchMachinesQuery();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    reset();
    return () => {
      reset();
    };
  }, []);

  const handleTextareaChange = event => setFeedbackText(event.target.value);

  const handleMediaUploaderUploadStart = () => setIsUploading(true);

  const handleMediaUploaderUploadEnd = () => setIsUploading(false);

  const handleMediaUploaderChange = mediaUrl => setMediaUrl(mediaUrl);

  const handleMediaUploaderBeforeRemove = () => {};

  const handleMediaUploaderError = error => {
    notification.error(formatMessage(messages.fileNotSupported));
    Sentry.captureException(error);
  };

  const selectLine = value => setSelectedLine(parseInt(value, 10), dispatchMachinesQuery);

  const selectMachine = value => setSelectedMachine(parseInt(value, 10));

  const selectType = value => setSelectedDispatchType(parseInt(value, 10));

  const formatOptions = (options, field) => {
    return options.map(option => {
      const {id, description} = option;
      return {
        id,
        name: option[field] + ' - ' + description
      };
    });
  };

  const dispatch = async () => {
    try {
      await createDispatch(openDispatchMutation);
      notification.success(formatMessage(messages.dispatchSubmitted));
      popSidePanel();
    } catch (error) {
      notification.error(error.message ? error.message : formatMessage(messages.somethingWentWrong));
      Sentry.captureException(error, {extra: {stepId: activeStepId}});
    }
  };

  return (
    <CreateDispatchPanelWrapper>
      <Description>{formatMessage(messages.createDispatchDescription)}</Description>
      <MainContentWrapper>
        <SectionWrapper>
          <Select
            label={formatMessage(messages.productionLine)}
            inputId={'line-selector'}
            placeholder={formatMessage(messages.selectLine)}
            options={formatOptions(lines, 'abbreviation')}
            onChange={selectLine}
            selectedValue={selectedLineId}
            showSearch={true}
            onGetToBottom={fetchMoreLines}
            loading={linesLoading}
          />
        </SectionWrapper>

        <SectionWrapper>
          <Select
            label={formatMessage(messages.dispatchMachine)}
            inputId={'dispatchMachine'}
            placeholder={formatMessage(messages.selectDispatchMachine)}
            options={formatOptions(dispatchMachines, 'code')}
            onChange={selectMachine}
            disabled={!selectedLineId}
            selectedValue={selectedMachineId}
            showSearch={true}
            onGetToBottom={fetchMoreMachines}
            loading={machinesLoading}
            feedbackText={!selectedLineId ? formatMessage(messages.selectLineFirst) : null}
          />
        </SectionWrapper>

        <SectionWrapper>
          <Select
            label={formatMessage(messages.dispatchType)}
            inputId={'dispatchTypes'}
            placeholder={formatMessage(messages.selectDispatchType)}
            options={formatOptions(dispatchTypes, 'code')}
            onChange={selectType}
            selectedValue={selectedDispatchTypeId}
            showSearch={true}
            onGetToBottom={fetchMoreTypes}
            loading={typesLoading}
          />
        </SectionWrapper>

        <SectionWrapper>
          <Label>{formatMessage(messages.details)}</Label>
          <ModalsTextBox
            dataCy={'create-dispatch-text'}
            isDesktop={isDesktop}
            handleTextareaChange={handleTextareaChange}
            placeholder={formatMessage(messages.describeIssue)}
            value={feedbackText}
            maxFeedbackLength={maxFeedbackLength}
          />
        </SectionWrapper>

        <MediaUploaderWrapper isDesktop={isDesktop}>
          <MediaUploader
            isDesktop={isDesktop}
            onUploadStart={handleMediaUploaderUploadStart}
            onUploadEnd={handleMediaUploaderUploadEnd}
            onBeforeRemove={handleMediaUploaderBeforeRemove}
            onChange={handleMediaUploaderChange}
            onError={handleMediaUploaderError}
            value={mediaUrl}
          />
        </MediaUploaderWrapper>
      </MainContentWrapper>
      <ButtonsContainer>
        <Button aria-label="dispatch" onClick={dispatch} loading={loading} disabled={!canOpenDispatch}>
          {formatMessage(messages.submitDispatch)}
        </Button>
        <Button
          aria-label="cancel"
          design="outline"
          onClick={() => {
            popSidePanel();
          }}
        >
          {formatMessage(messages.cancel)}
        </Button>
      </ButtonsContainer>
    </CreateDispatchPanelWrapper>
  );
};

export default withSidePanel(injectIntl(observer(CreateDispatchPanelComponent)));
